import { X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/Dialog';
import { Textarea } from '@shared/components/ui/Textarea';
import { cn } from '@shared/components/ui/cn';

const DEFAULT_MIN_SCORE = 0;
const DEFAULT_MAX_SCORE = 10;
const DEFAULT_COMMENT_MAX_LENGTH = 2000;

export interface EnpsSurveyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user submits a score (and optional comment). */
  onSubmit?: (score: number, comment: string) => void;
  /** Called when the user closes the survey without answering (Skip, X, Esc, overlay). */
  onSkip?: () => void;
  minScore?: number;
  maxScore?: number;
  commentMaxLength?: number;
}

// Figma 1.4 "Long comment — the field scrolls": a 4px rounded bar inset 3px
// from the edge (zinc-200 track, zinc-400 thumb), drawn inside the 12px right
// padding so the text column stays 436px. WebKit: a 10px gutter, always
// reserved, with transparent borders + padding-box clip for the inset. Only
// browsers without ::-webkit-scrollbar (Firefox) get the standard properties —
// in Chrome they would switch the WebKit styling off.
const textareaScrollbarClassName = cn(
  'pr-0.5 [scrollbar-gutter:stable]',
  'supports-[not_selector(::-webkit-scrollbar)]:pr-3 supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-color:var(--color-zinc-400)_var(--color-zinc-200)] supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-width:thin]',
  '[&::-webkit-scrollbar]:w-2.5',
  '[&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:border-x-[3px] [&::-webkit-scrollbar-track]:border-y-2 [&::-webkit-scrollbar-track]:border-transparent [&::-webkit-scrollbar-track]:bg-zinc-200 [&::-webkit-scrollbar-track]:bg-clip-padding',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-x-[3px] [&::-webkit-scrollbar-thumb]:border-y-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-400 [&::-webkit-scrollbar-thumb]:bg-clip-padding',
);

const contentClassName =
  'max-w-[512px] rounded-[10px] border-border p-6 shadow-[0px_12px_32px_-4px_rgba(0,0,0,0.12)] sm:rounded-[10px]';

function CloseIcon() {
  const { t } = useTranslation('common');
  return (
    <DialogClose className="size-6 shrink-0 rounded-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50">
      <X className="size-6" strokeWidth={1.5} aria-hidden />
      <span className="sr-only">{t('ui.close', 'Close')}</span>
    </DialogClose>
  );
}

/**
 * eNPS survey modal (DEV-4077, Figma "ENPS"). Presentational: scheduling and
 * persistence live in `useEnpsSurvey`, mounted once by `EnpsSurveyHost`.
 */
export function EnpsSurveyModal({
  open,
  onOpenChange,
  onSubmit,
  onSkip,
  minScore = DEFAULT_MIN_SCORE,
  maxScore = DEFAULT_MAX_SCORE,
  commentMaxLength = DEFAULT_COMMENT_MAX_LENGTH,
}: EnpsSurveyModalProps) {
  const { t, i18n } = useTranslation('enps');
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const scoreButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const successCloseRef = useRef<HTMLButtonElement | null>(null);
  const scores = Array.from({ length: maxScore - minScore + 1 }, (_, index) => minScore + index);

  useEffect(() => {
    if (open) {
      setScore(null);
      setComment('');
      setSubmitted(false);
    }
  }, [open]);

  // The focused Submit button unmounts on success; move focus to Close.
  useEffect(() => {
    if (submitted) successCloseRef.current?.focus();
  }, [submitted]);

  // Every way of closing the unanswered survey (Skip, X, Esc, overlay click)
  // is a dismiss — spec: it is recorded as a no response. Once the user has
  // started answering, a stray Esc or overlay click is ignored so the answer
  // isn't thrown away for the whole quarter; Skip and X still close it.
  const hasDraft = score !== null || comment.trim() !== '';
  const guardDraft = (event: Event) => {
    if (!submitted && hasDraft) event.preventDefault();
  };

  const handleSurveyOpenChange = (next: boolean) => {
    if (!next) onSkip?.();
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (score === null) return;
    onSubmit?.(score, comment.trim());
    setSubmitted(true);
  };

  const handleScoreKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    // In RTL the row is mirrored, so the visual meaning of Left/Right flips.
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';
    if (event.key === forward || event.key === 'ArrowDown') {
      nextIndex = Math.min(index + 1, scores.length - 1);
    } else if (event.key === backward || event.key === 'ArrowUp') {
      nextIndex = Math.max(index - 1, 0);
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = scores.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    setScore(scores[nextIndex] ?? null);
    scoreButtonRefs.current[nextIndex]?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={submitted ? onOpenChange : handleSurveyOpenChange}>
      <DialogContent
        showClose={false}
        overlayClassName="bg-black/50"
        onEscapeKeyDown={guardDraft}
        onInteractOutside={guardDraft}
        // Start on the scale, not on the X: Enter on an auto-focused X would
        // dismiss the quarter's survey by accident.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          scoreButtonRefs.current[0]?.focus();
        }}
        className={cn(contentClassName, submitted ? 'gap-4' : 'gap-5')}
      >
        {submitted ? (
          <>
            <div className="flex items-start justify-between">
              <img
                src="/enps/success-icon.svg"
                alt=""
                width={40}
                height={40}
                className="size-10 shrink-0"
              />
              <CloseIcon />
            </div>
            <DialogHeader className="gap-1.5 space-y-0 text-start">
              <DialogTitle className="text-lg leading-[26px] font-semibold tracking-normal">
                {t('success.title', 'Thanks — your score is recorded')}
              </DialogTitle>
              <DialogDescription className="leading-5">
                {t(
                  'success.description',
                  'We ask once a quarter, so you will not see this again until the next one.',
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                ref={successCloseRef}
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                {t('success.close', 'Close')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <DialogHeader className="min-w-0 flex-1 gap-1.5 space-y-0 text-start">
                <DialogTitle className="text-lg leading-[26px] font-semibold tracking-normal">
                  {t('title', 'How likely are you to recommend Investbanq to a colleague?')}
                </DialogTitle>
                <DialogDescription className="leading-5">
                  {t(
                    'description',
                    'Your answer helps us improve the platform. It takes less than a minute.',
                  )}
                </DialogDescription>
              </DialogHeader>
              <CloseIcon />
            </div>

            <div className="flex flex-col gap-2">
              <div
                className="flex w-full gap-1"
                role="radiogroup"
                aria-label={t('scaleAriaLabel', 'Score, {{min}} to {{max}}', {
                  min: minScore,
                  max: maxScore,
                })}
                aria-describedby="enps-scale-hint"
              >
                {scores.map((value, index) => {
                  const isSelected = score === value;
                  const isTabbable = score === null ? index === 0 : isSelected;

                  return (
                    <button
                      key={value}
                      ref={(node) => {
                        scoreButtonRefs.current[index] = node;
                      }}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={isTabbable ? 0 : -1}
                      onClick={() => {
                        setScore(value);
                      }}
                      onKeyDown={(event) => {
                        handleScoreKeyDown(event, index);
                      }}
                      className={cn(
                        'flex h-10 min-w-0 flex-1 items-center justify-center rounded-md border text-sm leading-5 font-medium outline-none transition-colors',
                        'focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-foreground hover:bg-accent',
                      )}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
              <div
                id="enps-scale-hint"
                className="flex w-full justify-between gap-4 text-xs leading-4 text-muted-foreground"
              >
                <span>{t('scaleMin', 'Not at all likely')}</span>
                <span className="text-end">{t('scaleMax', 'Extremely likely')}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="enps-comment"
                className="text-sm leading-5 font-medium text-foreground"
              >
                {t('commentLabel', 'What is the main reason for your score? (optional)')}
              </label>
              <Textarea
                id="enps-comment"
                value={comment}
                maxLength={commentMaxLength}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
                placeholder={t(
                  'commentPlaceholder',
                  'Tell us what works well, or what gets in your way.',
                )}
                className={cn(
                  'h-[88px] min-h-0 resize-none overflow-y-auto border-border py-2.5 pl-3 leading-5 shadow-none placeholder:text-zinc-400 md:text-sm dark:placeholder:text-zinc-500',
                  textareaScrollbarClassName,
                )}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                className="shadow-none"
                onClick={() => {
                  handleSurveyOpenChange(false);
                }}
              >
                {t('skip', 'Skip')}
              </Button>
              <Button
                disabled={score === null}
                onClick={handleSubmit}
                className="disabled:opacity-40"
              >
                {t('submit', 'Submit')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
